export interface GetFormStateArgs {
  formSelector?: string;
  withinSelector?: string;
}

const ASSISTANT_PANE_SELECTOR = '[data-testid="unified-chat"]';

function isInAssistantPane(element: Element): boolean {
  return !!element.closest(ASSISTANT_PANE_SELECTOR);
}

export interface FormFieldInfo {
  name: string;
  type: string;
  label?: string;
  value: string | null;
  required?: boolean;
  placeholder?: string;
  visible: boolean;
}

export interface FormStateResult {
  success: boolean;
  form?: {
    found: boolean;
    selector?: string;
    fields: FormFieldInfo[];
    buttons: Array<{
      text: string;
      type: string;
      disabled: boolean;
      visible: boolean;
    }>;
    isWizard: boolean;
    wizardInfo?: {
      currentStep: number;
      totalSteps: number;
      stepName?: string;
      stepTitle?: string;
      canGoNext: boolean;
      canGoBack: boolean;
      isLastStep: boolean;
    };
  };
  message: string;
}

/**
 * Get the current state of a form or wizard on the page.
 * This tool can identify fields, their values, and navigation buttons.
 */
export async function getFormState(args: GetFormStateArgs): Promise<FormStateResult> {
  // Get the container to search within
  const getContainer = (): HTMLElement | null => {
    if (args.withinSelector) {
      const container = document.querySelector(args.withinSelector);
      if (container && container instanceof HTMLElement) {
        return container;
      }
    }
    return document.body;
  };

  // Find the form element
  const findForm = (): HTMLFormElement | null => {
    const container = getContainer();
    if (!container) return null;

    if (args.formSelector) {
      const form = container.querySelector(args.formSelector);
      if (form && form instanceof HTMLFormElement) {
        return form;
      }
    } else {
      // Find first form in container, or look for wizard structure
      const form = container.querySelector("form");
      if (form && form instanceof HTMLFormElement) {
        return form;
      }
      // If no form, check for wizard structure (Wizard component)
      const wizard = container.querySelector('[class*="Wizard"], [data-wizard]');
      if (wizard) {
        // Wizard might not have a form element, but we can still inspect it
        return null;
      }
    }

    return null;
  };

  const container = getContainer();
  if (!container) {
    return {
      success: false,
      message: "Container not found",
    };
  }

  const form = findForm();

  // Detect wizard using data attributes (much more reliable)
  const wizardElement = container.querySelector("[data-wizard]");
  const isWizard = !!wizardElement;

  // Try to find wizard structure using data attributes
  let wizardInfo: NonNullable<FormStateResult["form"]>["wizardInfo"] | undefined;
  if (isWizard && wizardElement) {
    // Get wizard info from data attributes
    const totalSteps = parseInt(wizardElement.getAttribute("data-wizard-total-steps") || "1", 10);
    const currentStep = parseInt(wizardElement.getAttribute("data-wizard-current-step") || "0", 10);

    // Get step name from current step indicator
    const currentStepIndicator = container.querySelector(
      `[data-wizard-step-indicator][data-wizard-step-active="true"]`,
    );
    const stepName = currentStepIndicator?.getAttribute("data-wizard-step-name") || undefined;

    // Get step title and subtitle from wizard content
    const stepTitle = container.querySelector("[data-wizard-step-title]")?.textContent?.trim();
    const _stepSubtitle = container.querySelector("[data-wizard-step-subtitle]")?.textContent?.trim();

    // Check for navigation buttons using data attributes
    const nextButton = container.querySelector('[data-wizard-button="next"]');
    const backButton = container.querySelector('[data-wizard-button="back"]');
    const doneButton = container.querySelector('[data-wizard-button="done"]');

    wizardInfo = {
      currentStep,
      totalSteps,
      stepName: stepName || stepTitle || `Step ${currentStep + 1}`,
      stepTitle: stepTitle || undefined,
      canGoNext: !nextButton?.hasAttribute("disabled") && !!nextButton,
      canGoBack: !backButton?.hasAttribute("disabled") && !!backButton,
      isLastStep: !!doneButton || currentStep >= totalSteps - 1,
    };
  }

  // Find all form fields
  const fields: FormFieldInfo[] = [];
  // For wizards, search within the current step content area
  const wizardStepContent = isWizard ? container.querySelector("[data-wizard-step-content]") : null;
  const fieldsSearchContainer = form || wizardStepContent || container;

  // Find inputs, textareas, and selects
  const inputs = fieldsSearchContainer.querySelectorAll("input, textarea, select");
  for (const input of Array.from(inputs)) {
    if (
      input instanceof HTMLInputElement ||
      input instanceof HTMLTextAreaElement ||
      input instanceof HTMLSelectElement
    ) {
      if (isInAssistantPane(input)) continue;

      // Skip hidden inputs
      if (input.type === "hidden") continue;
      // Skip submit and button inputs (they're handled separately)
      if (input.type === "submit" || input.type === "button") continue;

      // Get field name - try name, id, aria-label, or find from label's for attribute
      let name = input.name || input.id || input.getAttribute("aria-label") || "";

      // If no name yet, try to find it from a label that points to this input
      if (!name) {
        // Find label by checking if any label's 'for' matches a potential id we could assign
        // Or find the closest label element
        const parentLabel = input.closest("label");
        if (parentLabel) {
          const labelFor = parentLabel.getAttribute("for");
          if (labelFor) {
            name = labelFor;
          }
        } else {
          // Look for a label with a 'for' attribute that might match this input
          const labels = fieldsSearchContainer.querySelectorAll("label[for]");
          for (const labelEl of Array.from(labels)) {
            const labelFor = labelEl.getAttribute("for");
            if (labelFor) {
              // Check if this input is near this label (same parent, siblings, or in same container)
              const labelParent = labelEl.parentElement;
              const inputParent = input.parentElement;
              // Same parent, or siblings (share same parent), or label's parent contains input
              if (
                labelParent === inputParent ||
                (labelParent?.parentElement === inputParent?.parentElement && labelParent !== inputParent) ||
                labelParent?.contains(input)
              ) {
                name = labelFor;
                break;
              }
            }
          }
        }
      }

      // If still no name, use aria-label as fallback
      if (!name) {
        name = input.getAttribute("aria-label") || "";
      }

      // Get label text
      let label: string | undefined;
      if (input.id) {
        const labelElement = fieldsSearchContainer.querySelector(`label[for="${input.id}"]`);
        label = labelElement?.textContent?.trim();
      }
      if (!label) {
        // Try to find label as parent or sibling
        const parentLabel = input.closest("label");
        if (parentLabel) {
          label = parentLabel.textContent?.trim();
        } else {
          const prevSibling = input.previousElementSibling;
          if (prevSibling && prevSibling.tagName === "LABEL") {
            label = prevSibling.textContent?.trim();
          } else {
            // Look for a label with matching 'for' attribute
            if (name) {
              const labelElement = fieldsSearchContainer.querySelector(`label[for="${name}"]`);
              label = labelElement?.textContent?.trim();
            }
          }
        }
      }
      // Fallback to aria-label if no label found
      if (!label) {
        label = input.getAttribute("aria-label") || undefined;
      }

      // Get value
      let value: string | null = null;
      if (input instanceof HTMLInputElement) {
        if (input.type === "checkbox" || input.type === "radio") {
          value = input.checked ? "true" : "false";
        } else {
          value = input.value || null;
        }
      } else if (input instanceof HTMLTextAreaElement) {
        value = input.value || null;
      } else if (input instanceof HTMLSelectElement) {
        value = input.value || null;
      }

      // Check if required
      const required = input.hasAttribute("required") || input.getAttribute("aria-required") === "true";

      // Check visibility
      const style = window.getComputedStyle(input);
      const visible =
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        style.opacity !== "0" &&
        input.offsetWidth > 0 &&
        input.offsetHeight > 0;

      // Only add field if we have a name (even if it's from aria-label)
      // We should always have a name at this point (from name, id, aria-label, or label's for attribute)
      fields.push({
        name: name || `field_${fields.length}`, // Fallback to indexed name if somehow still no name
        type: input.type || input.tagName.toLowerCase(),
        label: label || undefined,
        value,
        required,
        placeholder: input instanceof HTMLInputElement ? input.placeholder || undefined : undefined,
        visible,
      });
    }
  }

  // Find buttons
  // For wizards, buttons are in the footer (outside step content), so search the entire wizard container
  const buttons: NonNullable<FormStateResult["form"]>["buttons"] = [];
  const buttonsSearchContainer = isWizard && wizardElement ? wizardElement : fieldsSearchContainer;
  const allButtons = buttonsSearchContainer.querySelectorAll("button");
  for (const button of Array.from(allButtons)) {
    if (isInAssistantPane(button)) continue;

    const text = button.textContent?.trim() || button.getAttribute("aria-label") || "";
    if (text) {
      buttons.push({
        text,
        type: button.type || "button",
        disabled: button.disabled,
        visible:
          window.getComputedStyle(button).display !== "none" &&
          window.getComputedStyle(button).visibility !== "hidden",
      });
    }
  }

  return {
    success: true,
    form: {
      found: !!form || isWizard,
      selector: args.formSelector || (form ? "form" : isWizard ? "wizard" : undefined),
      fields,
      buttons,
      isWizard,
      wizardInfo,
    },
    message: `Found ${fields.length} field(s) and ${buttons.length} button(s)${isWizard ? ` in wizard step ${(wizardInfo?.currentStep ?? 0) + 1} of ${wizardInfo?.totalSteps ?? 1}` : ""}`,
  };
}
